import * as React from 'react';
import { Link } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '#root/lib/utils';
import styles from './button.module.scss';

/**
 * Variantes de botón usando class-variance-authority + SCSS modules
 */
const buttonVariants = cva(styles.button, {
  variants: {
    variant: {
      primary: styles.primary,
      secondary: styles.secondary,
      danger: styles.danger,
      ghost: styles.ghost,
      link: styles.link,
      outline: styles.outline,
      whatsapp: styles.whatsapp,
      match: styles.match,
      matchOutline: styles.matchOutline,
    },
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
      icon: styles.icon,
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Si true, muestra spinner y deshabilita el botón
   */
  loading?: boolean;
  /**
   * Icono a mostrar a la izquierda del label
   * @example <Button icon={<SearchIcon />}>Buscar</Button>
   */
  icon?: React.ReactNode;
  /**
   * Render como child component (Radix-style polymorphism)
   * Útil para: <Button asChild><Link to="/path">Text</Link></Button>
   */
  asChild?: boolean;
  /**
   * Ruta de navegación. Si se proporciona, el botón se renderiza como Link de react-router-dom
   * @example <Button to="/dashboard">Ir al Dashboard</Button>
   */
  to?: string;
  /**
   * Si true, el botón ocupa todo el ancho disponible
   * @example <Button fullWidth>Botón Ancho Completo</Button>
   */
  fullWidth?: boolean;
}

/**
 * Button Component - Design System (SCSS Modules)
 *
 * Componente de botón moderno con SCSS modules.
 * Soporta múltiples variantes, tamaños y estados.
 *
 * @example
 * // Primary button (default)
 * <Button>Guardar</Button>
 *
 * @example
 * // Button con icono a la izquierda
 * <Button icon={<SearchIcon />}>Buscar</Button>
 *
 * @example
 * // Secondary button, small size
 * <Button variant="secondary" size="sm">Cancelar</Button>
 *
 * @example
 * // Loading state
 * <Button loading>Procesando...</Button>
 *
 * @example
 * // Danger button con icono (destructive action)
 * <Button variant="danger" icon={<TrashIcon />}>Eliminar</Button>
 *
 * @example
 * // Button como Link (navegación interna con react-router)
 * <Button to="/dashboard">Ir al Dashboard</Button>
 *
 * @example
 * // Outline button como Link con icono
 * <Button variant="outline" to="/settings" icon={<SettingsIcon />}>Configuración</Button>
 *
 * @example
 * // Full width button
 * <Button fullWidth>Botón Ancho Completo</Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading,
      disabled,
      icon,
      children,
      asChild = false,
      to,
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    // Contenido común del botón (spinner, icon, children)
    const buttonContent = (
      <>
        {loading && (
          <div className={styles.spinner}>
            <svg
              className={styles.spinnerIcon}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true">
              <circle
                className={styles.spinnerCircle}
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
            </svg>
          </div>
        )}
        <div className={loading ? styles.contentLoading : styles.content}>
          {icon && icon}
          {children}
        </div>
      </>
    );

    // Si asChild es true, renderizar children directamente (Radix pattern)
    if (asChild) {
      return React.cloneElement(children as React.ReactElement, {
        className: cn(buttonVariants({ variant, size }), className, {
          [styles.fullWidth]: fullWidth,
        }),
        ref,
        ...props,
      });
    }

    // Si tiene prop "to", detectar si es URL externa o interna
    if (to) {
      const isExternal = /^https?:\/\//.test(to);

      // URL externa (http:// o https://) → usar <a> tag
      if (isExternal) {
        return (
          <a
            href={to}
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant, size }), className, {
              [styles.disabled]: disabled || loading,
              [styles.fullWidth]: fullWidth,
            })}
            onClick={(e) => {
              if (disabled || loading) {
                e.preventDefault();
              }
              props.onClick?.(e as any);
            }}
            ref={ref as any}
            {...(props as any)}>
            {buttonContent}
          </a>
        );
      }

      // URL interna → usar react-router Link
      return (
        <Link
          to={to}
          className={cn(buttonVariants({ variant, size }), className, {
            [styles.disabled]: disabled || loading,
            [styles.fullWidth]: fullWidth,
          })}
          onClick={(e) => {
            if (disabled || loading) {
              e.preventDefault();
            }
            props.onClick?.(e as any);
          }}
          ref={ref as any}
          {...(props as any)}>
          {buttonContent}
        </Link>
      );
    }

    // Default: renderizar como button
    return (
      <button
        className={cn(buttonVariants({ variant, size }), className, {
          [styles.fullWidth]: fullWidth,
        })}
        ref={ref}
        disabled={disabled || loading}
        {...props}>
        {buttonContent}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
